// REMIX HMR BEGIN
if (!window.$RefreshReg$ || !window.$RefreshSig$ || !window.$RefreshRuntime$) {
  console.warn('remix:hmr: React Fast Refresh only works when the Remix compiler is running in development mode.');
} else {
  var prevRefreshReg = window.$RefreshReg$;
  var prevRefreshSig = window.$RefreshSig$;
  window.$RefreshReg$ = (type, id) => {
    window.$RefreshRuntime$.register(type, "\"app/routes/_index.tsx\"" + id);
  }
  window.$RefreshSig$ = window.$RefreshRuntime$.createSignatureFunctionForTransform;
}
var _s = $RefreshSig$();
import * as __hmr__ from "remix:hmr";
if (import.meta) {
  import.meta.hot = __hmr__.createHotContext(
  //@ts-expect-error
  "app/routes/_index.tsx");
  import.meta.hot.lastModified = "1748317013820.3713";
}
// REMIX HMR END

import { json, redirect } from '@remix-run/cloudflare';
import { Form, useLoaderData, useNavigate } from '@remix-run/react';
import { useState } from 'react';
import invariant from 'tiny-invariant';
import { Button, ButtonLink } from '~/components/Button';
import { Disclaimer } from '~/components/Disclaimer';
import DropdownMenu from '~/components/DropdownMenu';
import { Input } from '~/components/Input';
import { Label } from '~/components/Label';
import { LogoutButton } from '~/components/LogoutButton';
import { generateRoomPath } from '~/hooks/useOrganizationRoomPath';
import { useUserMetadata } from '~/hooks/useUserMetadata';
import { ProfileImage } from '~/components/ProfileImage';
import { authenticator, initAuthEnvironment } from '~/services/auth.server';
import { ACCESS_AUTHENTICATED_USER_EMAIL_HEADER } from '~/utils/constants';
import getUsername from '~/utils/getUsername.server';
export const loader = async ({
  request,
  context
}) => {
  // Initialize auth so we can use the authenticator directly
  initAuthEnvironment(context);

  // 1. Try to get the email from Cloudflare Access header first
  let email = request.headers.get(ACCESS_AUTHENTICATED_USER_EMAIL_HEADER);
  let currentUser = null;

  // 2. If not present, try Google authenticated session (remix-auth)
  if (!email) {
    try {
      currentUser = await authenticator.isAuthenticated(request);
      email = currentUser?.email ?? null;
    } catch (_) {
      // ignore -- user not authenticated via Google
    }
  } else {
    // Try to get full user data even when using Access header
    try {
      currentUser = await authenticator.isAuthenticated(request);
    } catch (_) {
      // ignore
    }
  }
  let accountUrl = null;
  if (email) {
    const domain = email.split('@')[1] ?? '';
    if (domain === 'gmail.com') {
      accountUrl = '/free';
    } else if (domain === 'quora.com') {
      accountUrl = '/org/quora';
    } else {
      const orgSlug = domain.replace(/\.com$/i, '');
      accountUrl = `/org/${orgSlug}`;
    }
  }

  // Fallback to original behaviour
  const directoryUrl = context.USER_DIRECTORY_URL;
  const username = await getUsername(request, context);
  const usedAccess = request.headers.has(ACCESS_AUTHENTICATED_USER_EMAIL_HEADER);
  return json({
    username,
    usedAccess,
    directoryUrl,
    accountUrl,
    currentUser
  });
};
export const action = async ({
  request
}) => {
  const room = (await request.formData()).get('room');
  invariant(typeof room === 'string');
  return redirect(room.replace(/ /g, '-'));
};
export default function Index() {
  _s();
  const {
    username,
    usedAccess: _usedAccess,
    accountUrl,
    currentUser
  } = useLoaderData();
  const navigate = useNavigate();
  const {
    data
  } = useUserMetadata(username ?? '');
  const [_videoComplete, setVideoComplete] = useState(false);

  // Use Google profile photo if available
  const profilePhotoUrl = currentUser?.photos?.[0]?.value || data?.photoUrl;

  // We're no longer automatically redirecting when the video completes
  // Just track if the video has been viewed

  return <div className="flex flex-col h-full mx-auto relative">
			{/* User profile in top right */}
			{username && <div className="absolute top-4 right-4 z-10">
					<div className="flex items-center gap-2">
						<DropdownMenu.Root>
							<DropdownMenu.Trigger asChild>
								<button className="rounded-full focus:outline-none focus:ring-2 focus:ring-fuchsia-500">
									<ProfileImage src={profilePhotoUrl} displayName={data?.displayName || currentUser?.displayName} email={currentUser?.email || username} size="sm" className="cursor-pointer hover:opacity-90 transition-opacity" />
								</button>
							</DropdownMenu.Trigger>
							<DropdownMenu.Portal>
								<DropdownMenu.Content sideOffset={5} align="end">
									<DropdownMenu.Arrow />
									<div className="px-3 py-2 text-sm text-zinc-700 dark:text-zinc-300 border-b border-zinc-200 dark:border-zinc-700">
										<div className="font-medium">{data?.displayName}</div>
									</div>
									<DropdownMenu.Item asChild>
										<LogoutButton displayType="ghost" className="w-full text-left text-red-600 dark:text-red-400 px-2 py-1.5 rounded" />
									</DropdownMenu.Item>
								</DropdownMenu.Content>
							</DropdownMenu.Portal>
						</DropdownMenu.Root>
					</div>
				</div>}

			{/* Video section - Full screen with content overlay */}
			<div className="relative w-full h-full flex flex-col">
				{/* Video Background */}
				<div className="absolute inset-0 w-full h-full">
					<video className="w-full h-full object-cover" autoPlay playsInline muted onEnded={() => setVideoComplete(true)}>
						<source src="/clamo.mp4" type="video/mp4" />
						Your browser does not support the video tag.
					</video>
				</div>

				{/* Content overlay */}
				<div className="relative z-10 flex-1 flex flex-col justify-center items-center p-4 text-white bg-black bg-opacity-40">
					<div className="text-center space-y-6">
						<h1 className="text-4xl font-bold tracking-tight">
							Meetings made easy
						</h1>
						<p className="max-w-md mx-auto text-lg">
							Streamlined video conferencing for teams of all sizes
						</p>

						{/* Actions */}
						<div className="flex flex-col sm:flex-row gap-4 justify-center mt-6">
							{accountUrl ? <ButtonLink to={accountUrl} className="text-sm bg-gradient-to-r from-pink-400 to-fuchsia-500 hover:from-pink-500 hover:to-fuchsia-600 shadow-lg border-transparent">
									Go to My Account
								</ButtonLink> : <ButtonLink to="/login" className="text-sm bg-gradient-to-r from-pink-400 to-fuchsia-500 hover:from-pink-500 hover:to-fuchsia-600 shadow-lg border-transparent">
									Sign In
								</ButtonLink>}
							<ButtonLink to="/new" className="text-sm bg-zinc-700 hover:bg-zinc-800" onClick={e => {
              e.preventDefault();
              const orgPrefix = new URLSearchParams(window.location.search).get('org');
              const roomPath = generateRoomPath(orgPrefix);
              navigate(roomPath);
            }}>
								New Room
							</ButtonLink>
						</div>

						{/* Join room option */}
						<details className="cursor-pointer mt-4">
							<summary className="text-zinc-300">Or join a room</summary>
							<Form className="grid items-end gap-4 grid-cols-[1fr_auto] w-full pt-4 max-w-md mx-auto" method="post">
								<div className="space-y-2">
									<Label htmlFor="room" className="text-white">
										Room name
									</Label>
									<Input name="room" id="room" required placeholder="roomname or organization/roomname" className="bg-zinc-800 text-white border-zinc-700" />
								</div>
								<Button className="text-xs" type="submit" displayType="secondary">
									Join
								</Button>
							</Form>
						</details>
					</div>
				</div>

				{/* Footer */}
				<div className="relative z-10 bg-black bg-opacity-40">
					<Disclaimer className="p-4 text-white" />
				</div>
			</div>
		</div>;
}
_s(Index, "xREJ+YwKpMa7jpEJrbazUeNbYQ4=", false, function () {
  return [useLoaderData, useNavigate, useUserMetadata];
});
_c = Index;
var _c;
$RefreshReg$(_c, "Index");

window.$RefreshReg$ = prevRefreshReg;
window.$RefreshSig$ = prevRefreshSig;